import { CloudOff, Download, HardDriveDownload, Loader2, RefreshCw, ShieldCheck, WifiOff, Wifi } from "lucide-react";
import { useOfflineReady } from "@/hooks/use-offline-ready";
import { useOnlineStatus } from "@/hooks/use-online-status";

interface Props {
  /** Compact pill (no detail line or re-check button). */
  compact?: boolean;
  className?: string;
}

const OfflineReadyBadge = ({ compact = false, className = "" }: Props) => {
  const { isOnline } = useOnlineStatus();
  const {
    status,
    cachedAssets,
    requiredAssets,
    totalCacheEntries,
    shellCached,
    swActive,
    checking,
    downloading,
    progress,
    recheck,
    downloadAll,
  } = useOfflineReady();


  const config = {
    checking: {
      label: "Checking cache…",
      tone: "bg-muted text-muted-foreground",
      Icon: Loader2,
      spin: true,
    },
    ready: {
      label: "Ready for Offline Use",
      tone: "bg-success/15 text-success",
      Icon: ShieldCheck,
      spin: false,
    },
    partial: {
      label: "Partly cached",
      tone: "bg-accent/15 text-accent",
      Icon: HardDriveDownload,
      spin: false,
    },
    "not-ready": {
      label: "Not cached yet",
      tone: "bg-destructive/15 text-destructive",
      Icon: CloudOff,
      spin: false,
    },
    unsupported: {
      label: "Offline unsupported here",
      tone: "bg-muted text-muted-foreground",
      Icon: CloudOff,
      spin: false,
    },
  }[status];

  const { Icon } = config;

  const detail =
    status === "ready"
      ? `${cachedAssets}/${requiredAssets} app files + shell stored on this device (${totalCacheEntries} cached entries). Works with no signal.`
      : status === "partial"
        ? `${cachedAssets}/${requiredAssets} app files cached${shellCached ? "" : ", app shell missing"}. Stay online a moment and re-check.`
        : status === "not-ready"
          ? swActive
            ? "Offline engine installed but nothing stored yet. Keep the app open online for a few seconds, then re-check."
            : "Offline engine not registered. Offline mode only runs on tradestoolbelt.com — not the editor preview."
          : status === "unsupported"
            ? "This browser doesn't expose cache storage, so offline can't be verified."
            : "Reading this device's cache…";

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${config.tone} ${className}`}
        title={detail}
      >
        <Icon className={`w-3 h-3 ${config.spin ? "animate-spin" : ""}`} />
        {config.label}
      </span>
    );
  }

  return (
    <div className={`space-y-2.5 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${config.tone}`}
          >
            <Icon className={`w-3.5 h-3.5 ${config.spin ? "animate-spin" : ""}`} />
            {config.label}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
              isOnline ? "bg-secondary text-muted-foreground" : "bg-primary/15 text-primary"
            }`}
          >
            {isOnline ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {isOnline ? "Online" : "Offline"}
          </span>
        </div>
        <button
          type="button"
          onClick={recheck}
          disabled={checking}
          className="flex shrink-0 items-center gap-1 rounded-md border border-border bg-secondary px-2 py-1 text-[11px] font-medium text-foreground active:scale-95 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${checking ? "animate-spin" : ""}`} />
          Re-check
        </button>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{detail}</p>

      {status !== "unsupported" && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => void downloadAll()}
            disabled={downloading || !isOnline}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground active:scale-[0.98] disabled:opacity-50"
          >
            {downloading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            {downloading
              ? `Downloading… ${progress}%`
              : status === "ready"
                ? "Re-download offline files"
                : "Download all files for offline use"}
          </button>
          {downloading && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          {!isOnline && status !== "ready" && (
            <p className="text-[11px] text-muted-foreground">
              Connect to wifi or data to finish caching, then re-check.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineReadyBadge;
